import {
  Alert, Box, Chip, Stack, Tooltip, Typography,
} from '@mui/material';
import { useMemo } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslate } from 'react-admin';
import { Asset, SecurityInventoryCategory, CertificationCategory } from '@x-guard/xgac-types/xgac';
import { calculateDaysUntilDate } from '../../../utils/certificationsAndItemsExpiry';
import { convertTimeGranularityToDays } from '../../../utils/timeGranularityUtils';

type NextEmailAlertProps = {
  type: 'security-inventory';
  category: SecurityInventoryCategory | null;
  users: Asset[];
} | {
  type: 'certification';
  category: CertificationCategory | null;
  users: Asset[];
};

export const NextEmailAlert = (props: NextEmailAlertProps) => {

  const translate = useTranslate();
  const expiryDate = useWatch({ name: 'expiryDate' }) as string | undefined;
  const plannedRenewalDate = useWatch({ name: 'plannedRenewalDate' }) as string | undefined;

  const recipients = useMemo(() => {

    const peopleToNotifyIds = new Set(
      (props.category?.notifiedPeople.peopleToNotify ?? [])
        .map((person) => (typeof person === 'string' ? person : person._id)),
    );

    const users = props.users.filter((user) => peopleToNotifyIds.has(user._id)).map((user) => ({ id: user._id, label: user.name }));
    const emails = (props.category?.notifiedPeople.emails ?? []).map((email) => ({ id: `email:${email}`, label: email }));

    if (props.type === 'certification' && props.category?.notifiedPeople.isCertificationHolderNotified) {

      users.push({ id: 'certification-holder', label: translate('resources.certification-categories.fields.certified_user') });

    }

    return [...users, ...emails];

  }, [props.category, props.users, props.type, translate]);

  const daysUntilNextEmail = useMemo(() => {

    const daysUntilPlannedRenewalDate = plannedRenewalDate ? calculateDaysUntilDate(plannedRenewalDate) : null;
    if (daysUntilPlannedRenewalDate !== null && daysUntilPlannedRenewalDate >= 0) {

      return daysUntilPlannedRenewalDate;

    }

    if (!expiryDate || !props.category) {

      return null;

    }

    const daysUntilExpiry = calculateDaysUntilDate(expiryDate);
    const candidates = [
      ...props.category.reminderPeriods.map((reminderPeriod) => (
        daysUntilExpiry - convertTimeGranularityToDays(reminderPeriod)
      )),
      daysUntilExpiry,
    ].filter((daysUntilEmail) => daysUntilEmail >= 0);

    return candidates.length > 0 ? Math.min(...candidates) : daysUntilExpiry;

  }, [plannedRenewalDate, expiryDate, props.category]);

  if (!props.category || daysUntilNextEmail === null) {

    return null;

  }

  const recipientsLabel = recipients.length === 1
    ? recipients[0].label
    : translate('general.text.users', { count: recipients.length });

  const isCandidateExpired = daysUntilNextEmail < 0;
  const hasNoRecipients = recipients.length === 0;

  return (
    <Alert
      severity={hasNoRecipients || isCandidateExpired ? 'warning' : 'info'}
      color={hasNoRecipients || isCandidateExpired ? 'warning' : 'success'}
      variant="outlined"
      sx={{ alignItems: 'center' }}
    >
      <Box sx={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5,
      }}>
        {isCandidateExpired && (
          <Typography>
            {props.type === 'security-inventory'
              ? translate('general.text.will_not_be_sent_item_expired', { smart_count: Math.abs(daysUntilNextEmail) })
              : translate('general.text.will_not_be_sent_certification_expired', { smart_count: Math.abs(daysUntilNextEmail) })}
          </Typography>
        )}
        {!isCandidateExpired && hasNoRecipients && (
          <Typography>{translate('general.text.email_will_not_be_sent', { smart_count: recipients.length })}</Typography>
        )}
        {!isCandidateExpired && !hasNoRecipients && (
          <>
            <Typography>
              {daysUntilNextEmail === 0
                ? translate('general.text.will_be_sent_today')
                : translate('general.text.email_will_be_sent', { smart_count: daysUntilNextEmail })}
            </Typography>
            {recipients.length === 1 && <Typography>{recipientsLabel}</Typography>}
            {recipients.length > 1 && (
              <Tooltip
                title={
                  <Stack>
                    {recipients.map((recipient) => <span key={recipient.id}>{recipient.label}</span>)}
                  </Stack>
                }
              >
                <Chip label={recipientsLabel} size="small" />
              </Tooltip>
            )}
            <Typography>{translate('general.text.will_receive')}</Typography>
          </>
        )}
      </Box>
    </Alert>
  );

};
